export interface CoalesceOptions {
  /** Return false to skip a queued trailing fetch (e.g. the key lost its observers). */
  stillWanted?: () => boolean;
  /** Upper bound on one fetch; on expiry the signal is aborted and the entry rejects with TimeoutError. */
  timeoutMs?: number;
}

/**
 * Thrown to a caller whose trailing fetch was skipped because nothing observes
 * the key any more. Resolving with the active fetch's result instead would let
 * TanStack stamp pre-invalidation data as fresh; rejecting leaves the query
 * invalidated so its next mount refetches.
 */
export class CoalesceSkippedError extends Error {
  constructor(message = 'trailing fetch skipped: key has no observer') {
    super(message);
    this.name = 'CoalesceSkippedError';
  }
}

type Fetcher<T> = (signal: AbortSignal) => Promise<T>;

const swallow = (): undefined => undefined;

interface Entry<T> {
  active: Promise<T>;
  queued?: Promise<T>;
}

export interface InFlightCoalescer<T> {
  /**
   * Runs `fetcher` for `key`, or joins the key's single trailing fetch when one
   * is already active. The first overlapping caller's `fetcher` and `options`
   * govern the trailing fetch; later overlapping callers only await it, so
   * every call for one key must carry equivalent arguments.
   */
  run(key: string, fetcher: Fetcher<T>, options?: CoalesceOptions): Promise<T>;
}

/**
 * Bounds concurrency to one active fetch per key. A call that overlaps the
 * active fetch shares a single trailing fetch that starts after it settles, so
 * the trailing result post-dates every overlapping call. Entries are released
 * with an identity check so a replacement entry created by a trailing fetch is
 * never deleted by its predecessor's cleanup.
 */
export function createInFlightCoalescer<T>(): InFlightCoalescer<T> {
  const entries = new Map<string, Entry<T>>();

  const bounded = async (run: Fetcher<T>, timeoutMs?: number): Promise<T> => {
    const controller = new AbortController();
    // Promise boundary: a fetcher that throws synchronously still yields a
    // settled promise, so an entry can never be stranded.
    const work = Promise.resolve().then(async () => await run(controller.signal));
    if (timeoutMs === undefined) {
      return await work;
    }
    return await new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        const reason = new DOMException('fetch exceeded its deadline', 'TimeoutError');
        controller.abort(reason);
        reject(reason);
      }, timeoutMs);
      work.then(resolve, reject).finally(() => clearTimeout(timer));
    });
  };

  const release = (key: string, entry: Entry<T>): void => {
    if (entries.get(key) === entry) {
      entries.delete(key);
    }
  };

  const start = async (key: string, run: Fetcher<T>, options?: CoalesceOptions): Promise<T> => {
    const entry: Entry<T> = { active: bounded(run, options?.timeoutMs) };
    entries.set(key, entry);
    entry.active
      .finally(() => {
        if (entry.queued === undefined) {
          release(key, entry);
        }
      })
      .catch(swallow);
    return await entry.active;
  };

  return {
    async run(key: string, fetcher: Fetcher<T>, options?: CoalesceOptions): Promise<T> {
      const entry = entries.get(key);
      if (!entry) {
        return await start(key, fetcher, options);
      }
      if (entry.queued === undefined) {
        entry.queued = entry.active.catch(swallow).then(async () => {
          let wanted = true;
          try {
            wanted = options?.stillWanted?.() !== false;
          } catch {
            wanted = false;
          }
          if (!wanted) {
            throw new CoalesceSkippedError();
          }
          return await start(key, fetcher, options);
        });
        entry.queued.finally(() => release(key, entry)).catch(swallow);
      }
      return await entry.queued;
    },
  };
}
